import { HomeIcon, UserGroupIcon, ChartBarIcon, DocumentTextIcon, XMarkIcon, TableCellsIcon, CalendarIcon } from '@heroicons/react/24/outline';
import { Link, useLocation } from 'react-router-dom';
import { useAuthStore } from '../../store/authStore';
import { Dialog, Transition } from '@headlessui/react';
import { Fragment } from 'react';

interface SidebarProps {
  isOpen?: boolean;
  onClose?: () => void;
}

export const Sidebar = ({ isOpen = false, onClose }: SidebarProps) => {
  const location = useLocation();
  const user = useAuthStore(state => state.user);

  const navigation = [
    { name: 'Dashboard', href: '/dashboard', icon: HomeIcon },
    ...(user?.role === 'admin' ? [
      { name: 'Usuarios', href: '/users', icon: UserGroupIcon },
      { name: 'Estadísticas', href: '/stats', icon: ChartBarIcon },
      { name: 'Gestión de Datos', href: '/data-management', icon: TableCellsIcon },
      { name: 'Datos Mensuales', href: '/monthly-data', icon: CalendarIcon }
    ] : []),
    { 
      name: 'Informes', 
      href: user?.role === 'admin' ? '/reports' : `/property/${user?.propertyId}/report`, 
      icon: DocumentTextIcon 
    }
  ];

  const sidebarContent = (
    <div className="flex flex-col h-full">
      <div className="flex items-center justify-between h-16 px-4">
        <img 
          className="h-8 w-auto" 
          src="https://inmoconsultingltd.com/logos-intranet/logo-white.png" 
          alt="INMOHOST" 
        />
        {/* Botón de cierre solo visible en móviles */}
        {onClose && (
          <button 
            className="md:hidden text-white hover:text-gray-300"
            onClick={onClose}
          >
            <XMarkIcon className="h-6 w-6" />
          </button>
        )}
      </div>
      
      <nav className="flex-1 px-2 py-4 space-y-1">
        {navigation.map((item) => {
          const current = location.pathname === item.href;
          return (
            <Link
              key={item.name}
              to={item.href}
              onClick={onClose} // Cerrar sidebar en móviles al hacer clic en un enlace
              className={`${
                current
                  ? 'bg-[#2d3545] text-white'
                  : 'text-gray-300 hover:bg-[#2d3545] hover:text-white'
              } group flex items-center px-3 py-2 text-sm font-medium rounded-md`}
            >
              <item.icon
                className="mr-3 flex-shrink-0 h-5 w-5"
                aria-hidden="true"
              />
              {item.name}
            </Link>
          );
        })}
      </nav>

      <div className="flex-shrink-0 flex p-4 bg-[#2d3545]">
        <div className="flex-shrink-0 w-full group block">
          <div className="flex items-center">
            <div>
              <p className="text-sm font-medium text-white">{user?.name}</p>
              <p className="text-xs text-gray-300">{user?.role === 'admin' ? 'Administrador' : 'Propietario'}</p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );

  // Sidebar para pantallas medianas y grandes (fijo)
  const desktopSidebar = (
    <div className="hidden md:fixed md:inset-y-0 md:left-0 md:flex md:w-64 md:flex-col md:bg-[#1a1f2b]">
      {sidebarContent}
    </div>
  );

  // Sidebar para móviles (modal)
  const mobileSidebar = (
    <Transition.Root show={isOpen} as={Fragment}>
      <Dialog as="div" className="relative z-40 md:hidden" onClose={onClose || (() => {})}>
        <Transition.Child
          as={Fragment}
          enter="transition-opacity ease-linear duration-300"
          enterFrom="opacity-0"
          enterTo="opacity-100"
          leave="transition-opacity ease-linear duration-300"
          leaveFrom="opacity-100"
          leaveTo="opacity-0"
        >
          <div className="fixed inset-0 bg-gray-600 bg-opacity-75" />
        </Transition.Child>

        <div className="fixed inset-0 z-40 flex">
          <Transition.Child
            as={Fragment}
            enter="transition ease-in-out duration-300 transform"
            enterFrom="-translate-x-full"
            enterTo="translate-x-0"
            leave="transition ease-in-out duration-300 transform"
            leaveFrom="translate-x-0"
            leaveTo="-translate-x-full"
          >
            <Dialog.Panel className="relative flex-1 flex flex-col max-w-xs w-full bg-[#1a1f2b]">
              {sidebarContent}
            </Dialog.Panel>
          </Transition.Child>
          <div className="flex-shrink-0 w-14">
            {/* Espacio de fuerza para evitar que el contenido se comprima */}
          </div>
        </div>
      </Dialog>
    </Transition.Root>
  );

  return (
    <>
      {desktopSidebar}
      {mobileSidebar}
    </>
  );
};